import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { combineLatest, firstValueFrom, map } from 'rxjs';
import { PlatformPlatformModeService } from '@data/_generated-api/platform-api/services/platform-platform-mode.service';
import { SILENT_API_ERRORS } from '@data/_generated-api/http-interceptors/silent-api-errors';
import { USER_QUERIES } from '@/app/platform/platform-core/user/store/types';
import { USER_ACTIVATOR } from '@/app/platform/platform-core/user/activators/types';
import { hasFeature } from '@/app/support/support-access.service';

/** Platformmodi (issue #51); zelfde strings als AppConstants.PlatformModes in de backend. */
export type PlatformMode = 'normal' | 'fullAccessOnly' | 'readOnly';

export const PLATFORM_MODES: readonly PlatformMode[] = ['normal', 'fullAccessOnly', 'readOnly'];

/**
 * Recht "volledige toegang": wie het heeft, merkt van geen enkele modus iets. Zelfde GUID als
 * AppConstants.Features.PlatformFullAccessFeatureId; feature op de pagina Registratiebeheer.
 */
export const PLATFORM_FULL_ACCESS_FEATURE_ID = 'f011acce-0000-4000-8000-0000f011acce';

/** Onbekend of leeg = normaal, net als de backend. */
export function toPlatformMode(value: unknown): PlatformMode {
  return PLATFORM_MODES.includes(value as PlatformMode) ? (value as PlatformMode) : 'normal';
}

/**
 * Houdt de platformmodus bij voor de balk, de login- en dashboardmelding, de sync-pauze en het
 * beheerpaneel. Bronnen: het endpoint (bij opstarten, op de loginpagina, na elke hubverbinding en
 * na een weigering door de api) en het hubbericht PlatformModeChanged. De api handhaaft; deze
 * service bepaalt enkel wat de gebruiker te zien krijgt.
 */
@Injectable({ providedIn: 'root' })
export class PlatformModeService {
  private readonly api = inject(PlatformPlatformModeService);
  private readonly router = inject(Router);
  // Optioneel: op de loginpagina en in specs zonder user-store is er gewoon geen gebruiker
  private readonly userQueries = inject(USER_QUERIES, { optional: true });
  private readonly userActivator = inject(USER_ACTIVATOR, { optional: true });

  private readonly _mode = signal<PlatformMode>('normal');
  private pendingLoad?: Promise<void>;

  readonly mode = this._mode.asReadonly();
  readonly hasFullAccess: Signal<boolean> = this.granted(PLATFORM_FULL_ACCESS_FEATURE_ID);

  /** De modus treft deze gebruiker; wie volledige toegang heeft, merkt er niets van. */
  readonly appliesToMe = computed(() => this._mode() !== 'normal' && !this.hasFullAccess());
  readonly appsClosedForMe = computed(() => this._mode() === 'fullAccessOnly' && !this.hasFullAccess());
  readonly writesPaused = computed(() => this._mode() === 'readOnly' && !this.hasFullAccess());

  /**
   * Sleutel onder `platform.mode` voor de balk bovenaan, of undefined zonder balk. Wie volledige
   * toegang heeft, krijgt een eigen tekst: hij werkt gewoon verder, maar moet weten dat de modus aanstaat.
   */
  readonly bannerKey = computed<string | undefined>(() => {
    const mode = this._mode();
    if (mode === 'normal') {
      return undefined;
    }
    return this.hasFullAccess() ? `${mode}Admin` : mode;
  });

  /** Haalt de modus op (ook anoniem). Gelijktijdige aanvragen delen één call; een fout laat de stand staan. */
  load(): Promise<void> {
    if (!this.pendingLoad) {
      this.pendingLoad = this.fetchMode().finally(() => (this.pendingLoad = undefined));
    }
    return this.pendingLoad;
  }

  /** Beheer: modus instellen. De api pusht de wijziging ook naar alle andere tabs. */
  async set(mode: PlatformMode): Promise<boolean> {
    let result: { isValid?: boolean; value?: { mode?: string | null } | null } | undefined;
    try {
      result = await firstValueFrom(this.api.postPlatformModeActionSet({ body: { mode } }));
    } catch {
      return false;
    }
    if (!result?.isValid) {
      return false;
    }
    this.apply(toPlatformMode(result.value?.mode));
    return true;
  }

  apply(mode: PlatformMode): void {
    if (mode === this._mode()) {
      return;
    }
    this._mode.set(mode);
    void this.refreshUserAndLeaveClosedApp();
  }

  private async fetchMode(): Promise<void> {
    try {
      const context = new HttpContext().set(SILENT_API_ERRORS, true);
      const result = await firstValueFrom(this.api.getPlatformMode(undefined, context));
      if (result?.isValid) {
        this.apply(toPlatformMode(result.value?.mode));
      }
    } catch {
      // Stil: de modus is informatief, de api handhaaft zelf
    }
  }

  /**
   * De apps komen mee met de gebruikersinfo (de api laat ze weg in "alleen volledige toegang"):
   * meteen vernieuwen in plaats van op de volgende refresh (max 60 s). Een open app verlaten we
   * actief, want guards lopen enkel bij navigatie.
   */
  private async refreshUserAndLeaveClosedApp(): Promise<void> {
    if (!this.userQueries?.getIsAnyUserAuthenticated()) {
      return;
    }
    await this.userActivator?.renewAuthentication();
    if (this.appsClosedForMe() && this.router.url !== '/') {
      void this.router.navigateByUrl('/');
    }
  }

  private granted(featureId: string): Signal<boolean> {
    if (this.userQueries == null) {
      return computed(() => false);
    }
    return toSignal(
      combineLatest([this.userQueries.isAnyUserAuthenticated$, this.userQueries.availableApps$]).pipe(
        map(([authenticated, apps]) => authenticated && hasFeature(apps, featureId))
      ),
      { initialValue: false }
    );
  }
}
